import {
  ActionMenu,
  Button,
  Heading,
  Icon,
  Input,
  TextArea,
} from '@dittolive/anvil'
import { yupResolver } from '@hookform/resolvers/yup'
import { DotsThreeIcon, XIcon } from '@phosphor-icons/react'
import React, { useCallback, useMemo, useState } from 'react'
import {
  FormProvider,
  useFieldArray,
  useForm,
  useFormState,
} from 'react-hook-form'

import {
  getConnectionStringValue,
  MongoCollection,
  MongoConnectorFeatureFlags,
  MongoConnectorLink,
} from '../../../types/mongoConnector'
import SyncedCollection from '../components/SyncedCollection'
import AddOrEditCollectionModal from './AddOrEditCollectionModal'
import {
  ConnectorFormValues,
  MongoCollectionFormValue,
  mongoConnectorSchema,
} from './validation'

type Props = {
  /** Initial data for editing an existing connector */
  initialData?: Partial<MongoConnectorLink> | null
  /** Callback when form is submitted */
  onSubmit: (data: ConnectorFormValues) => void
  /** Feature flags that affect form behavior */
  featureFlags?: MongoConnectorFeatureFlags
  /** Optional slot for documentation link in collections section */
  docsLink?: React.ReactNode
}

export default function ConnectOrUpdateForm({
  initialData,
  onSubmit,
  featureFlags,
  docsLink,
}: Props) {
  const isUpdate = !!initialData
  const form = useForm<ConnectorFormValues>({
    defaultValues: {
      username: '',
      password: '',
      database: initialData?.database ?? '',
      connectionString: initialData?.connectionString
        ? getConnectionStringValue(initialData.connectionString)
        : '',
      collections: initialData?.collections
        ? initialData.collections.map(
            ({
              name,
              idMappingFields,
              initialSync,
              dqlStrictMode,
              mode,
              dittoTypes,
            }) => ({
              name,
              initialSync,
              dqlStrictMode: dqlStrictMode ?? false,
              idMappingFields: idMappingFields as string[],
              matchIDs:
                idMappingFields.length === 1 && idMappingFields.includes('_id'),
              mode: mode ?? 'NATIVE',
              dittoTypes: {
                settableCounters:
                  (dittoTypes?.settableCounters as string[]) || [],
              },
            }),
          )
        : [],
    },
    resolver: yupResolver(mongoConnectorSchema),
  })

  const collections = form.watch('collections')

  const { errors } = useFormState({ control: form.control })
  const { remove, append, update } = useFieldArray({
    control: form.control,
    name: 'collections',
  })

  const [editingCollectionIdx, setEditingCollectionIdx] = useState<
    number | null
  >(null)
  const editingCollection = useMemo(
    () =>
      editingCollectionIdx != null
        ? collections.at(editingCollectionIdx)
        : null,
    [collections, editingCollectionIdx],
  )

  const [addingCollection, setAddingCollection] = useState(false)

  const addOrUpdateCollection = useCallback(
    (value: MongoCollectionFormValue) => {
      if (editingCollectionIdx !== null) {
        update(editingCollectionIdx, value)
        setEditingCollectionIdx(null)
      } else {
        append(value)
        setAddingCollection(false)
      }
    },
    [append, editingCollectionIdx, update],
  )

  const onCloseAddOrEditCollection = useCallback(() => {
    if (editingCollectionIdx !== null) {
      setEditingCollectionIdx(null)
    } else {
      setAddingCollection(false)
    }
  }, [editingCollectionIdx])

  return (
    <FormProvider {...form}>
      <AddOrEditCollectionModal
        index={editingCollectionIdx}
        isOpen={addingCollection || editingCollection !== null}
        editingCollection={editingCollection}
        onSave={addOrUpdateCollection}
        onClose={onCloseAddOrEditCollection}
        featureFlags={featureFlags}
      />

      <form
        className="flex flex-col gap-6"
        data-testid="connectOrUpdateForm"
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <div>
          <Heading level={3}>Connection info</Heading>
          <p className="text-foreground-subtle text-base">
            Details of the MongoDB database you&apos;d like to connect.
          </p>
        </div>

        <fieldset className="flex flex-col gap-8">
          <Input
            id="database"
            label="Database name"
            description="The name of the MongoDB database you'd like to sync with Ditto"
            placeholder="my-database"
            {...form.register('database')}
            errorMessage={errors.database?.message}
            disabled={isUpdate}
            data-testid="database"
            title={
              isUpdate
                ? 'You may not update the database name once configured'
                : ''
            }
            data-sentry-mask
          />

          <TextArea
            id="connectionString"
            label="Connection string"
            description="The connection string from your MongoDB portal"
            placeholder="mongodb://username:password@host:port"
            {...form.register('connectionString')}
            errorMessage={errors.connectionString?.message}
            disabled={isUpdate}
            data-testid="connectionString"
            title={
              isUpdate
                ? 'You may not update the connection string directly. You may update the username and password below, if needed'
                : ''
            }
            data-sentry-mask
          />

          {isUpdate && (
            <Input
              id="username"
              label="Username"
              description="The username for the connection string"
              {...form.register('username')}
              errorMessage={errors.username?.message}
              data-testid="username"
            />
          )}

          {isUpdate && (
            <Input
              type="password"
              id="password"
              label="Password"
              description="The password for the connection string"
              {...form.register('password')}
              errorMessage={errors.password?.message}
              data-testid="password"
              data-sentry-mask
            />
          )}
        </fieldset>

        <div className="mt-11">
          <Heading level={3}>Collections to sync</Heading>
          <p className="text-foreground-subtle text-base">
            The MongoDB collections you&apos;d like to sync with Ditto.{' '}
            {docsLink}
          </p>
        </div>

        <div className="flex flex-col gap-2">
          {!!collections.length && (
            <div className="divide-border-normal border-border-normal flex flex-col divide-y rounded-lg border">
              {collections.map((collection, index) => (
                <SyncedCollection
                  key={`collection-${index}-${collection.name}`}
                  collection={collection as MongoCollection}
                  action={
                    <div className="flex items-center gap-2">
                      {!collection.initialSync ? (
                        <div className="bg-fill-warning-secondary text-fill-warning inline-flex items-center rounded-md px-1.5 py-0.5 text-sm font-medium">
                          <Icon svg={<XIcon />} className="mr-1 size-3" />
                          <span>Initial sync</span>
                        </div>
                      ) : null}

                      {collection.mode === 'EJSON' ? (
                        <div className="bg-fill-info-secondary text-fill-info inline-flex items-center rounded-md px-1.5 py-0.5 text-sm font-medium">
                          <span>Extended JSON</span>
                        </div>
                      ) : null}

                      <ActionMenu
                        icon={DotsThreeIcon}
                        items={[
                          {
                            label: 'Edit',
                            onClick: () => setEditingCollectionIdx(index),
                          },
                          { label: 'Delete', onClick: () => remove(index) },
                        ]}
                      />
                    </div>
                  }
                />
              ))}
            </div>
          )}

          <div className="flex">
            <Button
              variant="outline"
              className="rounded-lg"
              onClick={() => setAddingCollection(true)}
            >
              Add collection
            </Button>
          </div>
        </div>

        <div className="mt-4 flex">
          <Button variant="primary" type="submit" className="rounded-lg">
            {isUpdate ? 'Update' : 'Save'}
          </Button>
        </div>
      </form>
    </FormProvider>
  )
}

export type { ConnectorFormValues, MongoCollectionFormValue }
